import { addMonths, subMonths } from 'date-fns';
import { useMemo, useState } from 'react';
import type { Product } from '../../../data/product';
import { longMonthName } from '../../../helpers/months';
import { useProductCostHistory } from '../../../hooks/cost-history';
import { HoverCard } from '../../popover/hover-card';
import { defineColumn, type StrictColumnDef } from '../../tanstack-table/column';
import { CostHistorySection, type CostMetric } from './cost-history-chart';
import {
  computeBars,
  type SavingsLevel,
  SavingsTooltipContent,
} from './savings-indicator-internals';

const monthNames = [
  longMonthName(subMonths(new Date(), 1)),
  longMonthName(new Date()),
  longMonthName(addMonths(new Date(), 1)),
];

export const DealsSavingsIndicator: StrictColumnDef<Product> = {
  ...defineColumn<Product>({
    id: 'Deals Savings',
    minWidth: 45,
    accessorFn: () => '',
    cell: ({ item: product }) => <DealsSavingsCell product={product} />,
  }),
  header: '💰',
};

function DealsSavingsCell({ product }: { product: Product }) {
  const { bars, tooltipContent } = useMemo(() => {
    const bars = computeBars(
      { cost: product.pricing.previous_cost, min_cases: 0 },
      { cost: product.pricing.current_cost, min_cases: 0 },
      { cost: product.pricing.future_cost, min_cases: 0 }
    );

    const costsData = [
      {
        month: monthNames[0],
        deal: { cost: product.pricing.previous_cost, min_cases: 0 },
      },
      {
        month: monthNames[1],
        deal: { cost: product.pricing.current_cost, min_cases: 0 },
      },
      {
        month: monthNames[2],
        deal: { cost: product.pricing.future_cost, min_cases: 0 },
      },
    ];
    const tooltipContent = <SavingsTooltipContent costsData={costsData} showMinQty={false} />;

    return { bars, tooltipContent };
  }, [product]);

  if (bars.every((bar) => bar === 'NONE')) {
    return null;
  }

  // Deals indicator shows the frontline (single-case) cost in its table, so the
  // chart plots frontline too — table, line, and callout stay in agreement.
  return (
    <SavingsBars product={product} metric="frontline" bars={bars} tooltipContent={tooltipContent} />
  );
}

export const VolumeSavingsIndicator: StrictColumnDef<Product> = {
  ...defineColumn<Product>({
    id: 'Volume Savings',
    minWidth: 45,
    accessorFn: () => '',
    cell: ({ item: product }) => <VolumeSavingsCell product={product} />,
  }),
  header: '💰',
};

function VolumeSavingsCell({ product }: { product: Product }) {
  const { bars, tooltipContent } = useMemo(() => {
    const previousTier = product.pricing.previous_volume_discounts?.slice(-1)[0];
    const currentTier = product.pricing.volume_discounts?.slice(-1)[0];
    const futureTier = product.pricing.future_volume_discounts?.slice(-1)[0];

    const previousCost = {
      cost: previousTier?.cost ?? product.pricing.previous_cost,
      min_cases: previousTier?.min_cases ?? 0,
    };
    const currentCost = {
      cost: currentTier?.cost ?? product.pricing.current_cost,
      min_cases: currentTier?.min_cases ?? 0,
    };
    const futureCost = {
      cost: futureTier?.cost ?? product.pricing.future_cost,
      min_cases: futureTier?.min_cases ?? 0,
    };

    const bars = computeBars(previousCost, currentCost, futureCost);

    const costsData = [
      { month: monthNames[0], deal: previousCost },
      { month: monthNames[1], deal: currentCost },
      { month: monthNames[2], deal: futureCost },
    ];
    const tooltipContent = <SavingsTooltipContent costsData={costsData} showMinQty />;

    return { bars, tooltipContent };
  }, [product]);

  if (bars.every((bar) => bar === 'NONE')) {
    return null;
  }

  // Volume indicator shows the deepest-tier (lowest-achievable) cost in its
  // table, so the chart plots that same tier.
  return (
    <SavingsBars product={product} metric="lowest" bars={bars} tooltipContent={tooltipContent} />
  );
}

const getBarDisplay = (bar: SavingsLevel) => {
  switch (bar) {
    case 'NONE':
      return { symbol: '-', className: 'text-gray-400' };
    case 'EQUAL':
      return { symbol: '●', className: 'text-green-500' };
    case 'LOW':
      return {
        symbol: '▼',
        className: 'text-green-600 text-lg font-extrabold',
      };
    case 'HIGH':
      return { symbol: '▲', className: 'text-red-500 text-lg font-extrabold' };
    case 'VERY_HIGH':
      return { symbol: '▲', className: 'text-red-700 text-lg font-extrabold' };
  }
};

type SavingsBarsProps = {
  product: Product;
  metric: CostMetric;
  bars: SavingsLevel[];
  tooltipContent: React.ReactNode;
};

function SavingsBars({ product, metric, bars, tooltipContent }: SavingsBarsProps) {
  const [open, setOpen] = useState(false);
  const { costHistory, loading } = useProductCostHistory(product, { enabled: open });

  // Fixed width so the table, the chart, and the callout share one column and
  // the chart fills the popover instead of collapsing to its content width.
  const content = (
    <div className="w-80">
      {tooltipContent}
      <CostHistorySection costHistory={costHistory} loading={loading} metric={metric} />
    </div>
  );

  return (
    <HoverCard content={content} side="right" onOpenChange={setOpen}>
      <div className="flex h-4 gap-0.5 w-full">
        {bars.map((bar, index) => {
          const display = getBarDisplay(bar);

          return (
            <div key={index} className="flex-1">
              <div className={`h-full flex items-center justify-center ${display.className}`}>
                {display.symbol}
              </div>
            </div>
          );
        })}
      </div>
    </HoverCard>
  );
}
